/**
 * Historia 120 — Bloque 27 (Jupiter, ENMIENDA 06 / G3-10) --
 * `features/S233-ui-previsualizacion-y-barra-de-lote-jupiter.feature` (S238, S239) y
 * `features/S143-detalle-y-acciones-de-lote-jupiter.feature` (S143, S144).
 *
 * 🔴 EL HALLAZGO DEL PO: con dos lotes activos en la misma máquina, nada decía a qué lote pertenecía
 * cada pareja de botones. *"cuando hay más de un grupo no se diferencia el grupo"*. Este módulo es el
 * ÚNICO sitio donde se compone la identidad visible de un lote, para que la etiqueta de la barra de
 * acciones y el tooltip del distintivo de la lista digan literalmente lo mismo.
 *
 * 🔒 **SE DERIVA, NO SE PIDE** (F3/§8.4): el recuento y las órdenes salen del `batchId` que Sun ya
 * publica **por job** en el contexto activo — la misma derivación que `useBatchActions` usa para
 * agrupar. Jupiter **no calcula miembros**, **no llama a N6/N7 para pintar una etiqueta** y **no le
 * pide a Sun ningún campo nuevo**.
 *
 * 🔒 **D-SP24 — la parte traducible es UNA clave con interpolación**, `Batch of {{count}} jobs`.
 * ❌ Descartado por escrito componerla concatenando claves existentes (`Batch` · el número · `Jobs`):
 * concatenar palabras traducidas **no sostiene el orden en los 8 idiomas**, que es la misma razón por
 * la que esta historia nunca trocea literales. Las órdenes de fabricación se **anexan sin traducir**.
 */

/**
 * Los colores del distintivo por lote (S239). La paleta no cambia con la enmienda 08; lo que cambia es
 * CÓMO se elige uno de ellos — ver `batchBadgeColor`.
 */
export const BATCH_BADGE_COLORS = ['#7e57c2', '#26a69a', '#ef6c00', '#42a5f5', '#c2185b', '#8d6e63']

/**
 * S244 (ENMIENDA 08, G3-12 b) -- el color se deriva del IDENTIFICADOR del lote, nunca de su posición
 * en la lista.
 *
 * 🔴 POR QUÉ CAMBIÓ: antes se indexaba por el orden de aparición del lote en la lista de cada widget
 * (`BATCH_BADGE_COLORS[index % n]`). Los TRES widgets que listan jobs activos construyen su propia
 * lista, así que el MISMO lote salía de colores distintos en cada uno — justo lo contrario de lo que
 * el PO pidió: *"el color que se use para un grupo tiene que ser el mismo en los tres widgets"*.
 * Siendo una función pura del `batchId`, el color es el mismo en cualquier widget, en cualquier orden
 * y con cualquier subconjunto de jobs.
 *
 * ⚠️ LÍMITE DECLARADO, porque conviene que esté escrito y no descubrirlo en planta: con una paleta de
 * seis, dos lotes DISTINTOS pueden caer en el mismo color. No se puede evitar sin volver a hacer el
 * color dependiente de la lista, que es exactamente lo que este escenario prohíbe. Lo que distingue
 * sin ambigüedad en ese caso es la ETIQUETA del lote, que el tooltip ya lleva.
 *
 * @param {string} batchId
 * @returns {string}
 */
export const batchBadgeColor = (batchId = '') => {
  const key = String(batchId)
  // djb2: barata, estable y sin dependencias. Solo necesita repartir, no ser criptográfica.
  let hash = 5381
  for (let index = 0; index < key.length; index++) {
    hash = ((hash * 33) ^ key.charCodeAt(index)) >>> 0
  }
  return BATCH_BADGE_COLORS[hash % BATCH_BADGE_COLORS.length]
}

/**
 * S238 -- "Lote de N jobs · OF-1, OF-2". El número entra por INTERPOLACIÓN en la propia clave, nunca
 * partiendo la frase; las órdenes se anexan detrás.
 *
 * @param {{jobs: Array, fabricationOrderCodes: Array<string>}} group
 * @param {function(string, Object=): string} t_
 * @returns {string}
 */
export const formatBatchLabel = (group = {}, t_ = (key) => key) => {
  const jobCount = Array.isArray(group.jobs) ? group.jobs.length : 0
  const codes = Array.isArray(group.fabricationOrderCodes) ? group.fabricationOrderCodes : []
  return [t_('Batch of {{count}} jobs', { count: jobCount }), codes.join(', ')]
    .filter((part) => part !== '')
    .join(' · ')
}

/**
 * S239 -- el distintivo de cada lote presente entre los jobs activos: su color y su etiqueta (la MISMA
 * que la barra de acciones muestra, porque sale de `formatBatchLabel`). Un job sin lote no aparece
 * aquí, y por eso no lleva distintivo.
 *
 * @param {Array<Object>} batchGroups `useBatchActions().batchGroups`
 * @param {function(string, Object=): string} t_
 * @returns {Object} mapa `batchId -> {color, label}`
 */
export const buildBatchBadges = (batchGroups = [], t_ = (key) => key) => {
  const groups = Array.isArray(batchGroups) ? batchGroups : []
  return groups.reduce((badges, group) => {
    if (!group?.batchId) return badges
    return {
      ...badges,
      [group.batchId]: {
        // S244 -- del IDENTIFICADOR, no de la posición: el mismo lote sale del mismo color en los tres
        // widgets, aunque cada uno ordene o filtre su lista a su manera.
        color: batchBadgeColor(group.batchId),
        label: formatBatchLabel(group, t_)
      }
    }
  }, {})
}
